// GA4 -> BigQuery parity bridge.
//
// Every product event already flows through one of two GA4 choke points
// (`trackEvent` in src/utils/analytics.js, `safeLog` in useRmsAnalytics). This
// module translates those calls into canonical registry events so the same
// user action lands in both destinations from a single call site.
//
// Why a bridge instead of instrumenting each call site again:
//   - Parity is structural, not a discipline. A surface cannot drift into
//     sending GA4 an event that BigQuery never hears about, because both are
//     fed by the same call.
//   - GA4 is not severed. This is a dual-write; the GA4 payload is untouched.
//   - Adding coverage is a table entry, not a code change in a product file.
//
// Translation is deliberate rather than automatic: GA4 params are camelCase
// and loosely typed, registry payloads are snake_case with enums, and some
// values differ outright (MODULE_TYPES are snake_case in GA4 but camelCase in
// the registry). Every mapping is declared below; anything unmapped is simply
// not forwarded, so a new GA4 event never silently produces an unregistered
// BigQuery row. Payload validity is left to the registry validator in
// emitEvent — one source of truth for what is allowed.

import { emitAnalyticsEvent } from './emitEvent';
import { ACCOUNT_TYPES, RMS_SERVICE_USER_TYPES } from '../constants/userTypes';

// Registry enums that differ from the values GA4 call sites pass.
const MODULE_TYPE_VALUES = {
  clinical_interview: 'clinicalInterview',
  semi_structured_interview: 'semiStructuredInterview',
  diagnosis_decision: 'diagnosisDecision',
  post_assessment_discussion: 'postAssessmentDiscussion',
};

// Shape of a mapping:
//   event     - registry event name
//   fields    - { ga4ParamName: 'registry_field' } copied when defined
//   constants - fixed payload fields (used to split/merge GA4 event families)
//   values    - { registry_field: { ga4Value: registryValue } } value translation
//   derive    - (ga4Params) => extra payload fields, for computed values
//   product   - explicit product dimension override

// The registry records WHO an auth flow was for. GA4 call sites carry the
// account/user type instead, and the patient-prefixed event names encode it,
// so audience is derived rather than guessed.

// Lowercased, because a call site passes whatever casing its claims carry.
const RMS_SERVICE_USER_TYPES_LOWER = RMS_SERVICE_USER_TYPES.map((type) => type.toLowerCase());
const SERVICE_USER_ACCOUNT_TYPES_LOWER = [
  ACCOUNT_TYPES.ASSESSMENT_SERVICE_USER,
  ACCOUNT_TYPES.RMS_SERVICE_USER,
].map((type) => type.toLowerCase());

// Audience from what the call site said, or null when it said nothing about it.
export function audienceFromAccount(params = {}) {
  const userType = String(params.userType || '').toLowerCase();
  const accountType = String(params.accountType || '').toLowerCase();
  if (userType === 'patient' || userType === 'informant') return 'patient';
  // RMS service users (legal guardians, school representatives) share the
  // 'RMS ' prefix with the RMS provider roles, so they are named before it.
  if (RMS_SERVICE_USER_TYPES_LOWER.includes(userType)) return 'patient';
  if (userType.startsWith('rms')) return 'rms_provider';
  if (SERVICE_USER_ACCOUNT_TYPES_LOWER.includes(accountType)) return 'patient';
  return null;
}

// Audience from the surface the person is on. Prefixes come from src/routes.js
// and the first match wins, so the RMS provider routes are listed ahead of the
// RMS route family they sit inside.
//
// /set-up-mfa is deliberately absent: it is the second-factor setup screen for
// both Assessment providers and RMS providers, so the path alone cannot say
// which, and guessing there would be the same defect in a new place.
const SURFACE_AUDIENCES = [
  ['/rms/provider', 'rms_provider'], // /rms/provider-sign-in and /rms/provider/*
  ['/rms/', 'patient'], // RMS service users: legal guardians, school representatives
  ['/service-user/', 'patient'],
  ['/informant', 'patient'], // /informant-dashboard and /informant/*
  ['/patient-dashboard', 'patient'],
  ['/forms/', 'patient'],
  ['/scales/', 'patient'],
  ['/patient/', 'clinician'], // a clinician reading a patient record
  ['/dashboard', 'clinician'],
  ['/staff', 'clinician'],
  ['/onboarding', 'clinician'],
  ['/settings', 'clinician'],
  ['/analytics', 'clinician'],
  ['/td/admin', 'clinician'],
  ['/login', 'clinician'],
  ['/sign-up', 'clinician'],
  ['/set-up-password', 'clinician'],
  ['/verify-email', 'clinician'],
  ['/verify-action', 'clinician'],
];

export function audienceFromSurface(pathname) {
  const path = String(pathname || '').toLowerCase();
  const match = SURFACE_AUDIENCES.find(([prefix]) => path.startsWith(prefix));
  return match ? match[1] : null;
}

function currentPathname() {
  try {
    return typeof window !== 'undefined' ? window.location?.pathname || '' : '';
  } catch (_) {
    return '';
  }
}

// The surface is the primary signal because it is present on every call. The
// shared auth components (MFA dialogs, password setup, reset flows) mount on
// clinician, patient and RMS provider screens alike; when one of them omits a
// user type, deriving from the surface is what stops its patients and RMS
// providers being counted into the clinician activation funnel. An explicit
// account/user type still overrides it — it names the person the flow is for,
// which a shared component's URL cannot always tell us.
function audienceFrom(params = {}) {
  return (
    audienceFromAccount(params) || audienceFromSurface(currentPathname()) || 'clinician'
  );
}

const errorCodeFrom = (params = {}) =>
  String(params.code || params.error || params.error_code || params.error_type || 'unknown');

const deriveAudience = (params) => ({ audience: audienceFrom(params) });
const deriveErrorCode = (params) => ({ error_code: errorCodeFrom(params) });

// GA4 splits some auth events across a bare name whose audience has to be
// derived and a patient_-prefixed twin that encodes it. The registry keeps one
// event with an audience field, so the pair is declared once.
function audiencePair(ga4Name, mapping) {
  return {
    [ga4Name]: { ...mapping, derive: deriveAudience },
    [`patient_${ga4Name}`]: {
      ...mapping,
      constants: { ...(mapping.constants || {}), audience: 'patient' },
    },
  };
}

// Password setup: one registry event per outcome, audience carried as a field
// instead of three near-identical event-name families. The GA4 names are passed
// in per audience because they are irregular — the clinician invalid-link event
// carries no prefix, and two of the RMS names say rms_ where the others say
// rms_provider_ — so the payload shape is stated once and the names once.
const PASSWORD_SETUP_OUTCOMES = {
  success: { event: 'auth_password_setup_completed', constants: { already_complete: false } },
  alreadyComplete: {
    event: 'auth_password_setup_completed',
    constants: { already_complete: true },
  },
  error: { event: 'auth_password_setup_failed', derive: deriveErrorCode },
  invalidLink: { event: 'auth_password_setup_link_invalid', derive: deriveErrorCode },
};

function passwordSetupMappings(audience, ga4Names) {
  return Object.fromEntries(
    Object.entries(ga4Names).map(([outcome, ga4Name]) => {
      const { event, constants, derive } = PASSWORD_SETUP_OUTCOMES[outcome];
      return [
        ga4Name,
        { event, constants: { audience, ...constants }, ...(derive ? { derive } : {}) },
      ];
    }),
  );
}

export const GA4_MAPPINGS = {
  // --- Auth: sign-in, MFA and password setup -------------------------------
  // These are the activation funnel: how many people who are invited actually
  // get through sign-in, second factor and password setup.
  login_start: {
    event: 'auth_login_started',
    fields: { entryAccountType: 'entry_account_type' },
    derive: (params) => ({ method: params.method || 'email' }),
  },
  login_success: {
    event: 'auth_login_succeeded',
    fields: { entryAccountType: 'entry_account_type' },
    derive: (params) => ({ method: params.method || 'email' }),
  },
  login_error: {
    event: 'auth_login_failed',
    fields: { supportCode: 'support_code', entryAccountType: 'entry_account_type' },
    derive: (params) => ({
      method: params.method || 'email',
      error_code: errorCodeFrom(params),
    }),
  },
  verification_email_sent: {
    event: 'auth_verification_email_sent',
    derive: deriveAudience,
  },
  sign_up_error: {
    event: 'auth_signup_failed',
    derive: deriveErrorCode,
  },
  ...audiencePair('password_reset_requested', { event: 'auth_password_reset_requested' }),

  mfa_required: {
    event: 'auth_mfa_challenged',
    constants: { reused: false },
    fields: { mfaType: 'mfa_type', hintsCount: 'hints_count' },
  },
  mfa_complete: { event: 'auth_mfa_completed', fields: { mfaType: 'mfa_type' } },
  mfa_cancelled: { event: 'auth_mfa_cancelled', fields: { mfaType: 'mfa_type' } },
  mfa_resend: {
    event: 'auth_mfa_resent',
    fields: { supportCode: 'support_code' },
    derive: (params) => ({ throttled: Boolean(params.throttled) }),
  },
  ...audiencePair('mfa_totp_secret_generated', {
    event: 'auth_mfa_setup_started',
    constants: { method: 'totp' },
  }),
  ...audiencePair('mfa_setup_complete', {
    event: 'auth_mfa_setup_completed',
    constants: { already_enrolled: false },
    fields: { method: 'method' },
  }),
  ...audiencePair('mfa_setup_complete_already_enrolled', {
    event: 'auth_mfa_setup_completed',
    constants: { already_enrolled: true },
    fields: { method: 'method' },
  }),
  // Skipping setup is only offered to service users, so this name has no
  // audience-derived twin.
  patient_mfa_setup_skipped: {
    event: 'auth_mfa_setup_skipped',
    constants: { audience: 'patient' },
  },
  mfa_enrolment_failed: {
    event: 'auth_mfa_setup_failed',
    derive: (params) => ({
      method: params.method || 'totp',
      stage: params.stage || 'unknown',
      error_code: errorCodeFrom(params),
    }),
  },

  ...passwordSetupMappings('clinician', {
    success: 'clinician_password_setup_success',
    alreadyComplete: 'clinician_password_setup_already_complete',
    error: 'clinician_password_setup_error',
    invalidLink: 'password_setup_invalid_link',
  }),
  ...passwordSetupMappings('patient', {
    success: 'patient_password_setup_success',
    alreadyComplete: 'patient_password_setup_already_complete',
    error: 'patient_password_setup_error',
    invalidLink: 'patient_password_setup_invalid_link',
  }),
  ...passwordSetupMappings('rms_provider', {
    success: 'rms_provider_password_setup_success',
    alreadyComplete: 'rms_password_setup_already_complete',
    error: 'rms_provider_password_setup_error',
    invalidLink: 'rms_password_setup_invalid_link',
  }),

  // Microsoft SSO
  sso_microsoft_click: {
    event: 'auth_sso_started',
    constants: { provider: 'microsoft' },
    fields: { screen: 'screen', flow: 'flow', isMobile: 'is_mobile' },
  },
  sso_microsoft_success: {
    event: 'auth_sso_succeeded',
    constants: { provider: 'microsoft' },
    fields: { screen: 'screen' },
  },
  sso_microsoft_error: {
    event: 'auth_sso_failed',
    constants: { provider: 'microsoft' },
    fields: { screen: 'screen', flow: 'flow' },
    derive: deriveErrorCode,
  },
  sso_guard_signout: {
    event: 'auth_sso_guard_signout',
    fields: { screen: 'screen' },
    derive: (params) => ({ reason: params.reason || 'unknown' }),
  },

  // --- RMS: pre-account referral funnel ------------------------------------
  // The referral/form funnel milestones — rms_referral_intent_started,
  // rms_step_saved, rms_referral_intent_submitted, rms_form_started and
  // rms_form_submitted — are bridged server-side from the audit trail
  // (rms_analytics_bridge.py, surface='functions'), which is the single
  // source for those rows. Do not re-add frontend mappings for them here:
  // both halves fire on the same user action, so a second mapping doubles
  // every funnel count. The GA4 call sites are untouched — GA4 still
  // receives the raw events; only the BigQuery bridging is server-side.
  step_viewed: {
    event: 'rms_step_viewed',
    fields: {
      prospectId: 'prospect_id',
      patientRecordId: 'patient_record_id',
      formKey: 'form_key',
      schemaVersion: 'schema_version',
      stepId: 'step_id',
      sectionId: 'section_id',
      pathway: 'pathway',
      editReturnToReview: 'edit_return_to_review',
    },
  },
  // GA4 splits the eligibility outcome across two events; the registry keeps
  // one event with a result enum, so the funnel is a single row type.
  // The bare `eligibility_checked` GA4 event carries no outcome and is not
  // forwarded — and its outwardCode (a postcode district) deliberately does
  // not cross into the first-party stream.
  eligibility_passed: {
    event: 'rms_eligibility_checked',
    constants: { result: 'pass' },
    fields: { formKey: 'form_key', pathway: 'pathway', areaVersion: 'area_version' },
  },
  eligibility_failed: {
    event: 'rms_eligibility_checked',
    constants: { result: 'fail' },
    fields: {
      formKey: 'form_key',
      pathway: 'pathway',
      areaVersion: 'area_version',
      reason: 'reason',
    },
  },
  sign_in_invite_superseded: { event: 'rms_invite_superseded' },
  error_generic: {
    event: 'rms_client_error',
    fields: { where: 'where', code: 'error_code', stepId: 'step_id' },
  },

  // --- Assessment: provider surfaces ---------------------------------------
  // Two GA4 event names carry the pathway that the registry holds as a field.
  patient_overview_viewed: {
    event: 'assessment_overview_viewed',
    constants: { assessment_type: 'adhd' },
    fields: { patientId: 'patient_id' },
  },
  autism_patient_overview_viewed: {
    event: 'assessment_overview_viewed',
    constants: { assessment_type: 'autism' },
    fields: { patientId: 'patient_id' },
  },
  cyp_adhd_patient_overview_viewed: {
    event: 'assessment_overview_viewed',
    constants: { assessment_type: 'cyp_adhd' },
    fields: { patientId: 'patient_id' },
  },
  scales_preview_viewed: {
    event: 'assessment_scales_previewed',
    fields: { patientId: 'patient_id' },
  },
  assessment_module_card_clicked: {
    event: 'assessment_module_opened',
    fields: {
      patientId: 'patient_id',
      moduleType: 'module_type',
      action: 'action',
      progress: 'progress_pct',
    },
    values: { module_type: MODULE_TYPE_VALUES },
  },

  // --- Report bundle -------------------------------------------------------
  report_bundle_dialog_opened: {
    event: 'report_bundle_opened',
    fields: { patientId: 'patient_id', sectionCount: 'section_count', format: 'format' },
  },
  report_bundle_download_started: {
    event: 'report_bundle_download_started',
    fields: {
      patientId: 'patient_id',
      sections: 'sections',
      sectionCount: 'section_count',
      format: 'format',
    },
  },
  report_bundle_download_succeeded: {
    event: 'report_bundle_download_succeeded',
    fields: {
      patientId: 'patient_id',
      sections: 'sections',
      sectionCount: 'section_count',
      format: 'format',
      mode: 'mode',
    },
  },
  report_bundle_download_failed: {
    event: 'report_bundle_download_failed',
    fields: {
      patientId: 'patient_id',
      sections: 'sections',
      sectionCount: 'section_count',
      format: 'format',
      errorCode: 'error_code',
    },
  },

};

// GA4 event names that are intentionally not forwarded, so that "why is this
// missing" has a recorded answer rather than looking like an oversight.
export const UNBRIDGED_GA4_EVENTS = {
  page_view: 'GA4 collects page views natively; a duplicate first-party stream adds volume, not insight.',
  login: 'GA4-recommended name emitted after MFA; bridging it would double-count login_success.',
  mfa_required_reuse: 'Carries no factor type, and the challenge itself is already captured by mfa_required.',
  redirect_to_password_setup: 'Routing mechanics, not a milestone.',
  redirect_to_mfa_setup: 'Routing mechanics, not a milestone.',
  mfa_enroll_conflict: 'Diagnostic for an enrolment edge case; no registry counterpart agreed.',
  mfa_enroll_conflict_bypass: 'Diagnostic for an enrolment edge case; no registry counterpart agreed.',
  mfa_enroll_already_in_use_checked: 'Diagnostic probe, not a user milestone.',
  clinician_password_setup_account_not_found: 'Diagnostic branch; the failure is covered by the error event.',
  patient_password_setup_account_not_found: 'Diagnostic branch; the failure is covered by the error event.',
  rms_provider_setup_finalized: 'Provider invite finalisation has no registry event yet.',
  rms_provider_setup_finalize_failed: 'Provider invite finalisation has no registry event yet.',
  eligibility_checked: 'Carries no outcome (see eligibility_passed/failed) and its outwardCode is a postcode district.',
  referral_intent_started: 'Bridged server-side from the audit trail (rms_analytics_bridge.py); a frontend mapping would double the funnel.',
  step_saved: 'Bridged server-side from the audit trail (rms_analytics_bridge.py); a frontend mapping would double the funnel.',
  referral_intent_submitted: 'Bridged server-side from the audit trail (rms_analytics_bridge.py); a frontend mapping would double the funnel.',
  post_form_started: 'Bridged server-side from the audit trail (rms_analytics_bridge.py); a frontend mapping would double the funnel.',
  post_form_submitted: 'Bridged server-side from the audit trail (rms_analytics_bridge.py); a frontend mapping would double the funnel.',
  demo_visit: 'Public marketing surface; no registry event agreed yet.',
  // The twelve staff-administration outcome events are no longer bridged:
  // their five registry events are superseded by the server-side
  // staff_account_event, emitted inside the same audit writer as the Firestore
  // record. A browser emit dies with the tab, one of these paths sent no
  // parameters at all, and another hard-coded the reason flag regardless of
  // what was typed - all three problems are properties of WHERE they were
  // emitted. Bridging them again would double count against the server event.
  edit_staff_email_success: 'Superseded by the server-emitted staff_account_event (staff_account_audit.py).',
  edit_staff_email_duplicate_blocked: 'Superseded by the server-emitted staff_account_event (staff_account_audit.py).',
  edit_staff_email_failed: 'Superseded by the server-emitted staff_account_event (staff_account_audit.py).',
  resend_staff_welcome_success: 'Superseded by the server-emitted staff_account_event (staff_account_audit.py).',
  resend_staff_welcome_failed: 'Superseded by the server-emitted staff_account_event (staff_account_audit.py).',
  disable_staff_account_success: 'Superseded by the server-emitted staff_account_event (staff_account_audit.py).',
  disable_staff_account_failed: 'Superseded by the server-emitted staff_account_event (staff_account_audit.py).',
  enable_staff_account_success: 'Superseded by the server-emitted staff_account_event (staff_account_audit.py).',
  enable_staff_account_failed: 'Superseded by the server-emitted staff_account_event (staff_account_audit.py).',
  change_staff_account_type_success: 'Superseded by the server-emitted staff_account_event (staff_account_audit.py).',
  // NOT superseded: both block gates raise before any audit write, so the
  // server event never sees a blocked outcome. Blocked attempts are GA4-only
  // until the gates record an audit entry - a known coverage gap, not parity.
  change_staff_account_type_blocked: 'GA4-only: the server gates raise before any audit write, so staff_account_event cannot see blocks yet.',
  change_staff_account_type_failed: 'Superseded by the server-emitted staff_account_event (staff_account_audit.py).',
  edit_staff_email_opened: 'Dialog-opened events are UI mechanics, not milestones.',
  edit_staff_email_cancelled: 'Dialog-cancelled events are UI mechanics, not milestones.',
  resend_staff_welcome_clicked: 'Superseded by the success/failure outcome events.',
  disable_staff_account_opened: 'Dialog-opened events are UI mechanics, not milestones.',
  enable_staff_account_opened: 'Dialog-opened events are UI mechanics, not milestones.',
  change_staff_account_type_opened: 'Dialog-opened events are UI mechanics, not milestones.',
};

export function isBridgedGa4Event(ga4EventName) {
  return Object.prototype.hasOwnProperty.call(GA4_MAPPINGS, ga4EventName);
}

/**
 * Forward one GA4 event into the BigQuery stream. Returns true when a row was
 * queued. Never throws: a bridge failure must not disturb the GA4 call that
 * triggered it, nor the product flow behind that.
 */
export function bridgeGa4Event(ga4EventName, params = {}) {
  try {
    const mapping = GA4_MAPPINGS[ga4EventName];
    if (!mapping) return false;

    const payload = { ...(mapping.constants || {}) };
    Object.entries(mapping.fields || {}).forEach(([ga4Field, registryField]) => {
      const value = params[ga4Field];
      if (value === undefined || value === null || value === '') return;
      const translation = mapping.values?.[registryField];
      payload[registryField] = translation ? translation[value] ?? value : value;
    });
    if (mapping.derive) Object.assign(payload, mapping.derive(params));

    // A call site may declare which product it belongs to (HFP surfaces reuse
    // RMS form machinery), overriding the mapping's default. `product` is not
    // a mapped field, so it labels the row without entering the payload.
    return emitAnalyticsEvent(mapping.event, payload, {
      product: params.product || mapping.product,
    });
  } catch (_) {
    return false;
  }
}
